<div class="documentation-container">
  <mat-toolbar color="primary">
    <button mat-icon-button (click)="goBack()">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <span>Loan Documentation</span>
    <span class="toolbar-spacer"></span>
    <button mat-icon-button (click)="openInNewWindow()" [disabled]="loading || !!error || showAddressForm" matTooltip="Open in New Window">
      <mat-icon>open_in_new</mat-icon>
    </button>
  </mat-toolbar>

  <div class="content-container">
    @if (loading) {
      <div class="loading-container">
        <mat-spinner diameter="50"></mat-spinner>
        <p>Generating loan documentation...</p>
      </div>
    } @else if (error) {
      <mat-card class="error-card">
        <mat-card-header>
          <mat-card-title>Error</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <p>{{ error }}</p>
        </mat-card-content>
        <mat-card-actions>
          <button mat-button (click)="goBack()">Go Back</button>
        </mat-card-actions>
      </mat-card>
    } @else if (showAddressForm) {
      <div class="address-form-container">
        <mat-card>
          <mat-card-header>
            <mat-card-title>Address Information</mat-card-title>
            <mat-card-subtitle>
              Please provide the addresses to be used in the loan documentation.
              These addresses will not be saved to the database.
            </mat-card-subtitle>
          </mat-card-header>
          
          <mat-card-content>
            <form [formGroup]="addressForm" (ngSubmit)="onSubmitAddressForm()">
              <!-- Borrower Address Section -->
              <h2>Borrower Address</h2>
              <p>For: {{ contactName }}</p>
              
              <div class="form-row">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Street Address</mat-label>
                  <input matInput formControlName="borrowerStreet">
                </mat-form-field>
              </div>
              
              <div class="form-row three-column">
                <mat-form-field appearance="outline">
                  <mat-label>City</mat-label>
                  <input matInput formControlName="borrowerCity">
                </mat-form-field>
                
                <mat-form-field appearance="outline">
                  <mat-label>State</mat-label>
                  <mat-select formControlName="borrowerState">
                    <mat-option *ngFor="let state of states" [value]="state">
                      {{ state }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
                
                <mat-form-field appearance="outline">
                  <mat-label>ZIP Code</mat-label>
                  <input matInput formControlName="borrowerZip">
                </mat-form-field>
              </div>
              
              <!-- Bank Address Section -->
              <h2>Bank Address</h2>
              <p>For: {{ lenderName }}</p>
              
              <div class="form-row">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Street Address</mat-label>
                  <input matInput formControlName="bankStreet">
                </mat-form-field>
              </div>
              
              <div class="form-row three-column">
                <mat-form-field appearance="outline">
                  <mat-label>City</mat-label>
                  <input matInput formControlName="bankCity">
                </mat-form-field>
                
                <mat-form-field appearance="outline">
                  <mat-label>State</mat-label>
                  <mat-select formControlName="bankState">
                    <mat-option *ngFor="let state of states" [value]="state">
                      {{ state }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
                
                <mat-form-field appearance="outline">
                  <mat-label>ZIP Code</mat-label>
                  <input matInput formControlName="bankZip">
                </mat-form-field>
              </div>
              
              <div class="button-row">
                <button mat-button type="button" (click)="goBack()">Cancel</button>
                <button mat-raised-button color="primary" type="submit">Generate Document</button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>
      </div>
    } @else {
      <div class="documentation-content">
        <div class="loan-document">
          <div class="header">
            <h1>LOAN AGREEMENT</h1>
            <p>THIS LOAN AGREEMENT (this "Agreement") dated this {{ day }} day of {{ month }}, {{ year }}</p>
          </div>

          <div class="parties">
            <div class="party">
              <p><strong>BETWEEN:</strong></p>
              <p>
                {{ lenderName }}<br>
                {{ lenderStreet }}<br>
                @if (lenderCity || lenderState || lenderZip) {
                  {{ lenderCity }}@if (lenderCity && lenderState) {, }{{ lenderState }} {{ lenderZip }}<br>
                }
                (the "Lender")<br>
                OF THE FIRST PART
              </p>
            </div>

            <div class="party">
              <p><strong>AND</strong></p>
              <p>
                {{ contactName }}<br>
                {{ contactStreet }}<br>
                @if (contactCity || contactState || contactZip) {
                  {{ contactCity }}@if (contactCity && contactState) {, }{{ contactState }} {{ contactZip }}<br>
                }
                (the "Borrower")<br>
                OF THE SECOND PART
              </p>
            </div>
          </div>

          <p>IN CONSIDERATION OF the Lender loaning certain monies (the "Loan") to the Borrower, and the Borrower repaying the Loan to the Lender, the parties agree to keep, perform and fulfill the promises and conditions set out in this Agreement:</p>

          <div class="section">
            <p class="section-title">Loan Amount & Interest</p>
            <p>1. The Lender promises to loan {{ principalAmount }} to the Borrower and the Borrower promises to repay this principal amount to the Lender, with interest payable on the unpaid principal at the rate of {{ interestRate }} percent per annum, calculated monthly not in advance, beginning on {{ interestStartDate }}.</p>
          </div>

          <div class="section">
            <p class="section-title">Payment</p>
            <p>2. This Loan will be repaid in consecutive monthly installments of principal and interest of {{ monthlyPayment }} commencing on {{ paymentStartDate }} and continuing on the first of each following month until {{ paymentEndDate }} with the balance then owing under this Agreement being paid at that time.</p>
            <p>3. At any time while not in default under this Agreement, the Borrower may make lump sum payments or pay the outstanding balance then owing under this Agreement to the Lender without further bonus or penalty.</p>
          </div>

          <div class="section">
            <p class="section-title">Late Payment</p>
            <p>4. Should the Borrower fail to make a payment within {{ gracePeriod }} days of the date it is due, a late fee of {{ lateFee }} will be charged to the Borrower.</p>
          </div>

          <div class="section">
            <p class="section-title">Default</p>
            <p>5. Notwithstanding anything to the contrary in this Agreement, if the Borrower defaults in the performance of any obligation under this Agreement, then the Lender may declare the principal amount owing and interest due under this Agreement at that time to be immediately due and payable.</p>
            <p>6. Further, if the Lender declares the principal amount owing under this Agreement to be immediately due and payable, and the Borrower fails to provide full payment within {{ defaultGracePeriod }} days, the Borrower will be charged a {{ defaultLateFee }} late fee.</p>
            <p>7. If the Borrower defaults in payment as required under this Agreement or after demand for {{ demandPeriod }} days, the Security will be immediately provided to the Lender and the Lender is granted all rights of repossession as a secured party.</p>
          </div>

          <div class="section">
            <p class="section-title">Security</p>
            <p>8. The Borrower grants to the Lender a security interest in the Security until this Loan is paid in full. The Lender will be listed as a lender on the title of the Security whether or not the Lender elects to perfect the security interest in the Security. The Borrower will do everything necessary to assist the Lender in perfecting its security interest.</p>
          </div>

          <div class="section">
            <p class="section-title">Governing Law</p>
            <p>9. This Agreement will be construed in accordance with and governed by the laws of the State of {{ governingState }}.</p>
          </div>

          <div class="section">
            <p class="section-title">Costs</p>
            <p>10. The Borrower shall be liable for all costs, expenses and expenditures incurred including, without limitation, the complete legal costs of the Lender incurred by enforcing this Agreement as a result of any default by the Borrower and such costs will be added to the principal then outstanding and shall be due and payable by the Borrower to the Lender immediately upon demand of the Lender.</p>
          </div>

          <div class="section">
            <p class="section-title">Binding Effect</p>
            <p>11. This Agreement will pass to the benefit of and be binding upon the respective heirs, executors, administrators, successors and permitted assigns of the Borrower and Lender. The Borrower waives presentment for payment, notice of non-payment, protest, and notice of protest.</p>
          </div>

          <div class="section">
            <p class="section-title">Amendments</p>
            <p>12. This Agreement may only be amended or modified by a written instrument executed by both the Borrower and the Lender.</p>
          </div>

          <div class="section">
            <p class="section-title">Severability</p>
            <p>13. The clauses and paragraphs contained in this Agreement are intended to be read and construed independently of each other. If any term, covenant, condition or provision of this Agreement is held by a court of competent jurisdiction to be invalid, void or unenforceable, it is the parties' intent that such provision be reduced in scope by the court only to the extent deemed necessary by that court to render the provision reasonable and enforceable and the remainder of the provisions of this Agreement will in no way be affected, impaired or invalidated as a result.</p>
          </div>

          <div class="section">
            <p class="section-title">General Provisions</p>
            <p>14. Headings are inserted for the convenience of the parties only and are not to be considered when interpreting this Agreement. Words in the singular mean and include the plural and vice versa. Words in the masculine mean and include the feminine and vice versa.</p>
          </div>

          <div class="section">
            <p class="section-title">Entire Agreement</p>
            <p>15. This Agreement constitutes the entire agreement between the parties and there are no further items or provisions, either oral or otherwise.</p>
          </div>

          <p>IN WITNESS WHEREOF, the parties have duly affixed their signatures on this {{ signingDay }} day of {{ signingMonth }}, {{ signingYear }}.</p>

          <div class="signature">
            <p>SIGNED, SEALED, AND DELIVERED<br>
            this {{ signingDay }} day of {{ signingMonth }}, {{ signingYear }}.</p>
            <p>_____________________________<br>
            {{ lenderName }}</p>
          </div>

          <div class="signature">
            <p>SIGNED, SEALED, AND DELIVERED<br>
            this {{ signingDay }} day of {{ signingMonth }}, {{ signingYear }}.</p>
            <p>_____________________________<br>
            {{ contactName }}</p>
          </div>
        </div>
      </div>
    }
  </div>
</div>
